import { hasAdminRole, ViewType } from '../types/dashboard';
import {
	isPersonalViewPlan,
	PENDING_PERSONAL_OPTION_KEY,
	RESUMEN_OPTION_KEY
} from '../utils/viewOptions';
import {
	EMPLOYMENT_PLAN_KEY,
	orderedSelectablePlans,
	pendingPlanName,
	resolvePlanIdForView,
	resolvePendingPlanLogo,
	SelectablePlan,
	UNNAMED_PLAN_KEY
} from '../components/Navigation/planSelectorItems';
import { resolvePlanLogo } from '../components/Navigation/planDisplay';
import { navigateToPlan } from '../components/Navigation/planNavigation';
import {
	AdminAccessSummary,
	PlanKind,
	PlanStatus,
	PlanSummary
} from '../components/arca-nav/planTypes';

/**
 * Lo que la navegación necesita saber, resuelto en un solo sitio.
 *
 * Antes esto vivía DENTRO de las dos barras, `DesktopSidebarV2` y `MobileTopBarV2`, escrito dos
 * veces línea por línea: las mismas cincuenta líneas de derivación duplicadas. Dos copias de un
 * cálculo son dos cosas que pueden separarse, y aquí separarse significa que escritorio y móvil
 * respondan distinto a lo mismo.
 *
 * 🔴 Vive FUERA de `src/components/Navigation/` a propósito, y el análisis de la tarea lo
 * proponía dentro. No cabe ahí: este módulo es justo el que conoce `ViewType`, `hasAdminRole` y
 * `utils/viewOptions`, que es lo que el criterio de éxito prohíbe importar en `Navigation/`.
 * Ponerlo dentro habría dejado el criterio cumplido de mentira. `acoplamiento.test.ts` en
 * `Navigation/` vigila que no vuelva a entrar.
 *
 * Las barras son los bloques de Proa (`components/arca-nav`, Arca-Team/product#55): reciben todo
 * esto ya resuelto, con la forma de sus props (`PlanSummary`, `ControlDeNavegacion`), y no saben de
 * tipos de vista, ni de roles, ni de enrutado.
 */

export interface DatosDeNavegacion {
	/** Los planes, en el orden del selector y con la forma de Proa; el que está en proceso, al final. */
	plans: PlanSummary[];
	/** El plan que se está viendo. `undefined` en el Resumen: allí no se mira ningún plan. */
	activePlanId?: string;
	onSelectPlan: (plan: PlanSummary) => void;
	/** Se está en el Resumen multicuenta: el selector lo dice, con su icono de gráfica. */
	isSummaryActive: boolean;
	/** Ir al Resumen. Sin él (no hay Resumen), el selector no ofrece la fila. */
	onSelectSummary?: () => void;
	/** Se está mirando la empresa, no un plan propio. */
	isAdminActive: boolean;
	onSelectPersonal: () => void;
	onSelectAdmin: () => void;
	/** La empresa que administra (la de la sesión), o `null` sin rol de administración. */
	adminAccess: AdminAccessSummary | null;
	/**
	 * 🔴 `undefined` es «todavía no se sabe», y eso NO es «no tiene»: si la llamada de planes
	 * falla, quitar el cambio encerraría a un admin en la vista de empresa sin forma de volver.
	 * Una lista VACÍA sí es una respuesta.
	 */
	showPersonalAdminSwitch: boolean;
}

export interface EntradaDeNavegacion {
	/** Todos los planes (`GET /users/plans`). 🔴 `undefined` NO es lista vacía: ver arriba. */
	plans?: SelectablePlan[];
	viewType: string;
	roles?: string[];
	selectedPersonalPlanId?: string | null;
	/** Plan individual en proceso: alta del funnel viva sin plan personal todavía. */
	pendingPersonalPlan?: boolean;
	/** El plan que eligió en el funnel: nombre y logo de la fila en proceso. */
	pendingPlanArcaId?: string | null;
	/** Tiene Resumen multicuenta: va el primero del selector (chirigota#585). */
	resumenDisponible?: boolean;
	/** La empresa de la sesión, la que se administra (no la de un plan). */
	companyId?: string | null;
	companyName?: string | null;
	translate: (key: string) => string;
	setViewType: (type: string) => void;
	navigateToDefaultTab: (type: string) => void;
	onSelectPersonalPlan?: (planId: string) => void;
	/** Se abre un plan desde el selector (ver `navigateToPlan`). */
	onOpenPlan?: (planId: string) => void;
}

const kindOf = (plan: SelectablePlan): PlanKind => {
	if (plan.type === 'FREELANCE') return 'AUTONOMO';
	if (plan.type === 'EMPLOYMENT') return 'EMPLEO';
	if (plan.type === 'INDIVIDUAL') return 'INDIVIDUAL';
	// Sin tipo, el mismo criterio que el resto del Dashboard: por su empresa.
	return isPersonalViewPlan(plan) ? 'INDIVIDUAL' : 'EMPLEO';
};

/** Un plan de `GET /users/plans` con la forma que pide Proa. */
const aPlanDeProa = (plan: SelectablePlan, translate: (key: string) => string): PlanSummary => {
	const kind = kindOf(plan);
	return {
		id: plan.id,
		kind,
		// El de empleo con su nombre genérico; Proa le añade la empresa («Plan de empleo · Empresa»).
		name:
			kind === 'EMPLEO'
				? translate(EMPLOYMENT_PLAN_KEY)
				: plan.planDisplayName ?? translate(UNNAMED_PLAN_KEY),
		entity: (kind === 'EMPLEO' ? plan.companyName?.trim() : plan.managerName) || '',
		status: plan.status === 'suspended' ? PlanStatus.SUSPENDED : PlanStatus.ACTIVE,
		// «—» y no «0 €» cuando no se sabe: sin valor.
		valor: plan.vestedRightsCents === null ? undefined : plan.vestedRightsCents / 100,
		// 🔴 Del NOMBRE del plan, no de su gestora (ver `planDisplay.ts`).
		logoSrc: resolvePlanLogo(plan.planDisplayName, plan.managerName)
	};
};

export const buildNavigationData = ({
	plans,
	viewType,
	roles,
	selectedPersonalPlanId,
	pendingPersonalPlan = false,
	pendingPlanArcaId,
	resumenDisponible = false,
	companyId,
	companyName,
	translate,
	setViewType,
	navigateToDefaultTab,
	onSelectPersonalPlan,
	onOpenPlan
}: EntradaDeNavegacion): DatosDeNavegacion => {
	const livePlans = orderedSelectablePlans(plans ?? []);
	// 🔴 Qué plan se está viendo lo decide la VISTA, no sólo el plan personal preseleccionado.
	const shownPlanId = resolvePlanIdForView(livePlans, viewType, selectedPersonalPlanId, {
		pendingPersonalPlan
	});
	const isSummaryActive = shownPlanId === RESUMEN_OPTION_KEY;
	// El MISMO criterio que usa el Dashboard para su lista de planes personales.
	const personalPlan = livePlans.find(isPersonalViewPlan);

	const goTo = (target: string) => {
		setViewType(target);
		navigateToDefaultTab(target);
	};
	const abrir = (planId: string) =>
		navigateToPlan(livePlans, planId, {
			setViewType,
			navigateToDefaultTab,
			onSelectPersonalPlan,
			onOpenPlan
		});

	const planes = livePlans.map((plan) => aPlanDeProa(plan, translate));
	if (pendingPersonalPlan) {
		// El plan individual en proceso todavía no es un plan: va al final, «En proceso».
		planes.push({
			id: PENDING_PERSONAL_OPTION_KEY,
			kind: 'INDIVIDUAL',
			name: pendingPlanName(pendingPlanArcaId, translate),
			entity: '',
			status: PlanStatus.IN_PROGRESS,
			logoSrc: resolvePendingPlanLogo(pendingPlanArcaId)
		});
	}

	return {
		plans: planes,
		activePlanId: isSummaryActive ? undefined : shownPlanId ?? undefined,
		onSelectPlan: (plan) => abrir(plan.id),
		isSummaryActive,
		onSelectSummary: resumenDisponible ? () => abrir(RESUMEN_OPTION_KEY) : undefined,
		isAdminActive: viewType === ViewType.ADMIN,
		// Con 2+ planes, Personal es el Resumen, no un plan que no ha elegido (Marisa, 22/10/2026).
		onSelectPersonal: () =>
			goTo(
				resumenDisponible ? ViewType.RESUMEN : personalPlan ? ViewType.INDIVIDUAL : ViewType.USER
			),
		onSelectAdmin: () => goTo(ViewType.ADMIN),
		// 🔴 La empresa de la SESIÓN: antes la pastilla de Admin tomaba el nombre del primer plan
		// con empresa, que puede ser la de un empleo anterior y no la que se administra.
		adminAccess: hasAdminRole(roles)
			? {
					companies: [
						{
							id: companyId ?? '',
							// Sin nombre, vacío: los textos dicen entonces «Admin» a secas (`navegacionProa`).
							name: companyName?.trim() || '',
							totalSavedEur: 0
						}
					]
				}
			: null,
		showPersonalAdminSwitch: plans === undefined || livePlans.length > 0
	};
};
